/**
 * TenantAccessToggles — the "Company tenant" and "Multi-user login"
 * switches, extracted from SuperAdminPage so the tenant workspace
 * (Access & login tab) and any legacy surface share ONE implementation.
 *
 * Both auto-save through superAdminService and report the updated Tenant
 * back through `onChanged` so the caller can patch its local copy.
 *
 * They render as AccessRows — the same left-label/right-control line the rest
 * of the Access tab uses — rather than the tinted, self-margined boxes they
 * used to be. A switch is a setting like any other; it should not look like a
 * separate widget that wandered onto the page.
 */

import { useState } from 'react';
import { Switch, message } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import superAdminService from '@modules/super_admin/services/superAdminService';
import type { Tenant } from '@modules/super_admin/services/superAdminService';
import AccessRow from './AccessRow';

export function CompanyTenantToggle({ tenant, onChanged }: { tenant: Tenant; onChanged: (updated: Tenant) => void }) {
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const toggle = async (checked: boolean) => {
    setLoading(true);
    try {
      const result = await superAdminService.setTenantAsCompany(tenant.tenant_id, checked);
      const updated = { ...tenant, is_company: result.is_company };
      onChanged(updated);
      if (checked && result.descendants_updated > 0) {
        message.success(
          t('superAdmin.workspace.access.companyEnabledCascade')
            .replace('{{count}}', String(result.descendants_updated)),
        );
      } else if (checked) {
        message.success(t('superAdmin.workspace.access.companyEnabled'));
      } else {
        message.success(t('superAdmin.workspace.access.companyDisabled'));
      }
    } catch {
      message.error(t('superAdmin.workspace.access.companyFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AccessRow
      compact
      testId="access-row-company"
      label={t('superAdmin.workspace.access.companyTitle')}
      hint={t('superAdmin.workspace.access.companyDesc')}
      // The inheritance note is a consequence of the switch being ON, so it
      // only appears then — and full width, because it is about the subtree,
      // not about this control.
      footnote={tenant.is_company ? t('superAdmin.workspace.access.companyInherit') : undefined}
    >
      <Switch
        checked={!!tenant.is_company}
        onChange={toggle}
        loading={loading}
        data-testid="access-toggle-company"
      />
    </AccessRow>
  );
}

export function MultiUserLoginToggle({ tenant, onChanged }: { tenant: Tenant; onChanged: (updated: Tenant) => void }) {
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const toggle = async (checked: boolean) => {
    setLoading(true);
    try {
      const result = await superAdminService.setTenantMultiUserLogin(tenant.tenant_id, checked);
      onChanged({ ...tenant, multi_user_login_allowed: result.multi_user_login_allowed });
      message.success(t(checked
        ? 'superAdmin.workspace.access.multiUserEnabled'
        : 'superAdmin.workspace.access.multiUserDisabled',
      ));
    } catch {
      message.error(t('superAdmin.workspace.access.multiUserFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Only meaningful on company-flagged tenants. The backend rejects
  // the request otherwise, but we also hide the control so it never
  // appears for a non-company row.
  if (!tenant.is_company) return null;

  return (
    <AccessRow
      compact
      testId="access-row-multi-user"
      label={t('superAdmin.workspace.access.multiUserTitle')}
      hint={t('superAdmin.workspace.access.multiUserDesc')}
    >
      <Switch
        checked={!!tenant.multi_user_login_allowed}
        onChange={toggle}
        loading={loading}
        data-testid="access-toggle-multi-user"
      />
    </AccessRow>
  );
}
